import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Card, CardContent, CardActions, Typography, Button, CircularProgress, Grid,
    Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core';
import { useDataProvider, useNotify, Title } from 'react-admin';

const triggerBrowserDownload = (file, filename) => {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString() : '—';

const STATUS_COLORS = {
    completed: '#2e7d32',
    failed: '#c62828',
    processing: '#ef6c00',
    pending: '#616161',
};

const ExportsList = ({ exports, onDownload, downloadingId, onDelete, deletingId }) => {
    if (!exports || exports.length === 0) {
        return <Typography variant="body2" color="textSecondary">No exports yet.</Typography>;
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Requested</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {exports.map(exp => {
                    const isFinished = exp.status === 'completed' || exp.status === 'failed';

                    return (
                        <TableRow key={exp.id}>
                            <TableCell>{formatDateTime(exp.created_at)}</TableCell>
                            <TableCell>
                                <span style={{ color: STATUS_COLORS[exp.status] ?? '#616161', fontWeight: 500, textTransform: 'capitalize' }}>
                                    {exp.status}
                                </span>
                                {exp.status === 'failed' && (
                                    <Typography variant="caption" display="block" color="textSecondary">
                                        No signed contracts were available to export.
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {exp.status === 'completed' && (
                                    <Button
                                        size="small"
                                        color="primary"
                                        disabled={downloadingId === exp.id}
                                        onClick={() => onDownload(exp.id)}
                                    >
                                        {downloadingId === exp.id ? <CircularProgress size={16} /> : 'Download'}
                                    </Button>
                                )}
                                {isFinished && (
                                    <Button
                                        size="small"
                                        disabled={deletingId === exp.id}
                                        onClick={() => onDelete(exp.id)}
                                    >
                                        {deletingId === exp.id ? <CircularProgress size={16} /> : 'Delete'}
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

const POLL_INTERVAL_MS = 5000;

const ContractsExportCard = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [exports, setExports] = useState([]);
    const [starting, setStarting] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const intervalRef = useRef(null);

    const loadExports = useCallback(() => {
        return dataProvider.getList('contracts/downloads', { filter: {}, pagination: {}, sort: {} })
            .then(({ data }) => {
                setExports(data);
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error loading exports', 'error');
            });
    }, [dataProvider, notify]);

    useEffect(() => {
        loadExports();

        intervalRef.current = setInterval(loadExports, POLL_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [loadExports]);

    const handleStart = () => {
        setStarting(true);
        dataProvider.create('contracts/download', { data: {} })
            .then(() => loadExports())
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error starting export', 'error');
            })
            .finally(() => setStarting(false));
    };

    const handleDownload = (id) => {
        setDownloadingId(id);
        dataProvider.getFile(`contracts/downloads/${id}/file`)
            .then(({ data, filename }) => {
                triggerBrowserDownload(data, filename);
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error downloading export', 'error');
            })
            .finally(() => setDownloadingId(null));
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this export? This cannot be undone.')) {
            return;
        }

        setDeletingId(id);
        dataProvider.delete('contracts/downloads', { id })
            .then(() => loadExports())
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error deleting export', 'error');
            })
            .finally(() => setDeletingId(null));
    };

    return (
        <Card style={{ minWidth: 360 }}>
            <CardContent>
                <Typography variant="h6">Signed Contracts Export</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    Generates a zip of every signed contract for this production. Runs in the background —
                    you can leave this page and come back once it's ready.
                </Typography>
                <ExportsList
                    exports={exports}
                    onDownload={handleDownload}
                    downloadingId={downloadingId}
                    onDelete={handleDelete}
                    deletingId={deletingId}
                />
            </CardContent>
            <CardActions>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={starting}
                    onClick={handleStart}
                    startIcon={starting ? <CircularProgress size={16} /> : null}
                >
                    {starting ? 'Starting…' : 'Generate New Export'}
                </Button>
            </CardActions>
        </Card>
    );
};

const DirectDownloadCard = ({ title, description, resource }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const handleClick = () => {
        setLoading(true);
        dataProvider.getFile(resource)
            .then(({ data, filename }) => {
                triggerBrowserDownload(data, filename);
            })
            .catch(err => {
                notify(err?.body?.message ?? err?.message ?? 'Error generating report', 'error');
            })
            .finally(() => setLoading(false));
    };

    return (
        <Card style={{ minWidth: 280 }}>
            <CardContent>
                <Typography variant="h6">{title}</Typography>
                <Typography variant="body2" color="textSecondary">{description}</Typography>
            </CardContent>
            <CardActions>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    onClick={handleClick}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? 'Working…' : 'Download'}
                </Button>
            </CardActions>
        </Card>
    );
};

export const ReportsPage = () => (
    <div style={{ padding: 16 }}>
        <Title title="Reports" />
        <Grid container spacing={2}>
            <Grid item>
                <ContractsExportCard />
            </Grid>
            <Grid item>
                <DirectDownloadCard
                    title="Holiday Balances"
                    description="Export current holiday balances for all employments."
                    resource="reports/holiday-balances"
                />
            </Grid>
            <Grid item>
                <DirectDownloadCard
                    title="Capped Allowance Tracker"
                    description="Export the capped allowance tracker for all employments."
                    resource="reports/capped-allowance-tracker"
                />
            </Grid>
        </Grid>
    </div>
);
